import {
    ContractMessage,
    ContractState,
    EffectiveDateMode,
    PriceListActivationMode,
} from '../../../../types/ContractMessage';
import { Link } from '../../../cdl/Link/Link';
import { Text } from '../../../cdl/Text/Text';
import { DataSheet } from '../../../common/DataSheet/DataSheet';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../common/ui/Box';
import { PaymentTermsDisplay } from '../../../contracts/detail/components/PaymentTermsDisplay';

interface ValidityLabelProps {
    validFrom?: string;
    validUntil?: string;
    evergreen: boolean;
}

const getValidityLabel = ({ validFrom, validUntil, evergreen }: ValidityLabelProps) => {
    if (!validFrom) {
        return undefined;
    }

    const formattedValidFrom = formatDate({ date: validFrom });

    if (!validUntil) {
        return `${formattedValidFrom} ${Characters.RIGHTWARDS_ARROW} ${translate('contracts.detail.evergreenValue')}`;
    }

    const formattedValidUntil = formatDate({ date: validUntil });
    const validityLabel = `${formattedValidFrom} ${Characters.RIGHTWARDS_ARROW} ${formattedValidUntil}`;

    if (evergreen) {
        return `${validityLabel} ${translate('contracts.detail.evergreen')}`;
    }

    return validityLabel;
};

interface ContractDataSheetProps {
    contract: ContractMessage;
}

export const ContractDataSheet = ({ contract }: ContractDataSheetProps) => {
    const isDraft = contract.state === ContractState.DRAFT;
    const draftFallback = isDraft ? Characters.EN_DASH : undefined;
    const usesEnquiryDate = contract.effectiveDateMode === EffectiveDateMode.ORDER_DATE;
    const usesManualActivation = contract.priceListActivationMode === PriceListActivationMode.MANUAL;
    const effectiveDateModeLabel = translate(
        usesEnquiryDate ? 'contracts.detail.orderDate' : 'contracts.detail.deliveryDate'
    );
    const effectiveDateModeDescription = translate(
        usesEnquiryDate
            ? 'contracts.priceLists.effectiveDateMode.orderDateDescription'
            : 'contracts.priceLists.effectiveDateMode.deliveryDateDescription'
    );
    const activationModeLabel = translate(
        usesManualActivation ? 'contracts.detail.activationModeManual' : 'contracts.detail.activationModeAutomatic'
    );
    const activationModeDescription = translate(
        usesManualActivation
            ? 'contracts.priceLists.activationMode.manualDescription'
            : 'contracts.priceLists.activationMode.automaticDescription'
    );

    return (
        <DataSheet>
            <DataSheet.Row
                label={translate('contracts.detail.contractReference')}
                value={contract.contractReference ?? draftFallback}
            />
            <DataSheet.Row
                label={translate('contracts.labels.supplier')}
                value={contract.supplier?.name ?? draftFallback}
            />
            <DataSheet.Row
                label={translate('contracts.detail.validity')}
                value={
                    getValidityLabel({
                        validFrom: contract.validFrom,
                        validUntil: contract.validUntil,
                        evergreen: contract.evergreen,
                    }) ?? draftFallback
                }
            />
            <DataSheet.Label>{translate('contracts.detail.applyPricesBasedOn')}</DataSheet.Label>
            <Box display="flex" alignItems="center" gap={4}>
                <Text>{effectiveDateModeLabel}</Text>
                <InfoPopover width="250px">
                    <Text variant="extraSmall">{effectiveDateModeDescription}</Text>
                </InfoPopover>
            </Box>
            <DataSheet.Label>{translate('contracts.detail.activationMode')}</DataSheet.Label>
            <Box display="flex" alignItems="center" gap={4}>
                <Text>{activationModeLabel}</Text>
                <InfoPopover width="250px">
                    <Text variant="extraSmall">{activationModeDescription}</Text>
                </InfoPopover>
            </Box>
            {contract.paymentTermReference || isDraft ? (
                <>
                    <DataSheet.Label>{translate('contracts.detail.paymentTerms')}</DataSheet.Label>
                    {contract.paymentTermReference ? (
                        <PaymentTermsDisplay
                            paymentTermReference={contract.paymentTermReference}
                            paymentTermReferenceDays={contract.paymentTermReferenceDays}
                        />
                    ) : (
                        <Text>{draftFallback}</Text>
                    )}
                </>
            ) : null}
            {contract.gtc || isDraft ? (
                <>
                    <DataSheet.Label>{translate('contracts.detail.gtc')}</DataSheet.Label>
                    {contract.gtc ? (
                        <Link href={contract.gtc} size="small">
                            {contract.gtc}
                        </Link>
                    ) : (
                        <Text>{draftFallback}</Text>
                    )}
                </>
            ) : null}
            {contract.description || isDraft ? (
                <>
                    <DataSheet.Label>{translate('contracts.detail.description')}</DataSheet.Label>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{contract.description ?? draftFallback}</Text>
                </>
            ) : null}
        </DataSheet>
    );
};
